import { NavLink } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { MenuIcon } from 'lucide-react';
import { ease } from '../../../core/format/motion';
import type { AdminNavItem } from '../../adminNav';
import type { NavCounts } from './useNavCounts';
type Props = {
  tabs: AdminNavItem[];
  /** Het onderdeel waar je nu bent; staat het niet in de balk, dan licht "Menu" op. */
  current: AdminNavItem | undefined;
  menuOpen: boolean;
  dirty: boolean;
  /** Wat er wacht (Kassa, Inbox). Inbox staat in het menu: dan een stipje op Menu. */
  counts?: NavCounts;
  onMenu: () => void;
};
const itemCls = (active: boolean) => `relative flex h-full w-full flex-col items-center justify-center gap-1 whitespace-nowrap text-[11px] transition-colors duration-150 ease-soft ${active ? 'text-clay' : 'text-muted'}`;

/**
 * De tabbalk onderin op de telefoon: wat Sarah het vaakst nodig heeft, plus Menu voor de rest.
 * Altijd één tab actief, ook op een scherm uit het menu (dan "Menu", met de naam van dat scherm),
 * zodat je nooit kwijt bent waar je zit.
 */
export function AdminTabBar({
  tabs,
  current,
  menuOpen,
  dirty,
  counts = {},
  onMenu
}: Props) {
  const reduce = useReducedMotion();
  const inMenu = !current || !tabs.some(t => t.key === current.key);
  // Wachtend werk achter "Menu" (bijv. de inbox): een getal op de Menu-knop.
  const waitingInMenu = Object.entries(counts).reduce((s, [key, n]) => tabs.some(t => t.key === key) ? s : s + (n ?? 0), 0);
  const indicator = <motion.span layoutId="admin-tab" className="absolute inset-x-5 top-0 h-0.5 rounded-full bg-clay" transition={reduce ? {
    duration: 0
  } : {
    duration: 0.22,
    ease
  }} aria-hidden="true" data-id="element-5479" data-mp="3nemgbvx8pxp5" />;
  return <nav aria-label="Snel naar" className="fixed inset-x-0 bottom-0 z-nav border-t border-line bg-shell/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden" data-id="element-5480" data-mp="3ee7u8ucgcekl">
      <ul className="grid h-16" style={{
      gridTemplateColumns: `repeat(${tabs.length + 1}, minmax(0, 1fr))`
    }} data-id="element-5481" data-mp="1cl0exkwu9wyp">
        {tabs.map(t => <li key={t.key} data-id="element-5482" data-mp="1b2tcd1yvtlhf">
            <NavLink to={t.to} end={t.path === ''} className={({
          isActive
        }) => itemCls(isActive)} data-id="element-5483" data-mp="1662xuj2q6xy4">
              {({
            isActive
          }) => <>
                  {isActive && indicator}
                  <t.icon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-5484" data-mp="w7vig4o34ny8" />
                  {t.label}
                  {(counts[t.key] ?? 0) > 0 && <span className="absolute left-1/2 top-2 ml-2 min-w-[16px] rounded-full bg-clay px-1 text-center text-[10px] leading-4 tabular-nums text-shell" data-id="element-5485" data-mp="1tuma19owbuvh">
                      {counts[t.key]}
                      <span className="sr-only" data-id="element-5486" data-mp="1k3hqdwo9zlub"> wachten</span>
                    </span>}
                </>}
            </NavLink>
          </li>)}
        <li data-id="element-5487" data-mp="2cn6klb0q9dah">
          <button type="button" onClick={onMenu} aria-haspopup="dialog" aria-expanded={menuOpen} aria-current={inMenu && current ? 'page' : undefined} aria-label={`Volledig menu openen${inMenu && current ? `, nu bij ${current.label}` : ''}${waitingInMenu ? `, ${waitingInMenu} wachten` : ''}${dirty ? ' (niet-opgeslagen wijzigingen)' : ''}`} className={itemCls(inMenu && Boolean(current))} data-id="element-5488" data-mp="2cnyhqq9z3j2v">
            {inMenu && current && indicator}
            <MenuIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-5489" data-mp="2z2x7a10zh0oq" />
            {/* Op een menuscherm de naam van dat scherm, ingekort: zo zie je waar je bent. */}
            <span className="max-w-full truncate px-1" data-id="element-5490" data-mp="331w93crf4hby">{inMenu && current ? current.label.split(' ')[0] : 'Menu'}</span>
            {waitingInMenu > 0 ? <span className="absolute left-1/2 top-2 ml-2 min-w-[16px] rounded-full bg-clay px-1 text-center text-[10px] leading-4 tabular-nums text-shell" aria-hidden="true" data-id="element-5491" data-mp="3csqvch4nzgxe">
                {waitingInMenu}
              </span> : dirty && <span className="absolute right-[28%] top-3 h-1.5 w-1.5 rounded-full bg-clay" aria-hidden="true" data-id="element-5492" data-mp="ndz4644g6zn1" />}
          </button>
        </li>
      </ul>
    </nav>;
}